import { useState, useEffect } from 'react'
import { Gauge, Info, RefreshCw, Loader2 } from 'lucide-react'
import { rrGetWorkload } from '@/actions/rr'

type Status = 'in' | 'break' | 'out'

interface CapCell { used: number; cap: number }
interface AgentRow {
  user_id: number; name: string; status: Status; since: string
  abc: [CapCell, CapCell, CapCell]
  reorder: [CapCell, CapCell, CapCell]
  rto: [CapCell, CapCell, CapCell]
}

const AV_COLORS = ['#2f9e6b','#3b82c4','#b9772e','#8a5cd1','#c2557a','#2aa1a8']
function avColor(name: string) { let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0]??'')+(p[1]?.[0]??'')).toUpperCase() }

function lvl(used: number, cap: number): 'ok' | 'warn' | 'full' {
  const pct = cap > 0 ? used / cap : 0
  if (pct >= 1) return 'full'
  if (pct >= 0.7) return 'warn'
  return 'ok'
}

const CAP_COLORS = {
  ok:   { bar: 'bg-green-500',  text: 'text-green-700' },
  warn: { bar: 'bg-amber-500',  text: 'text-amber-700' },
  full: { bar: 'bg-red-500',    text: 'text-red-600' },
}

function CapBar({ cell, label }: { cell: CapCell; label: string }) {
  const level = lvl(cell.used, cell.cap)
  const pct = Math.min(100, cell.cap > 0 ? Math.round((cell.used / cell.cap) * 100) : 0)
  const c = CAP_COLORS[level]
  return (
    <div className="grid grid-cols-[28px_40px_1fr] items-center gap-2">
      <span className="text-[10px] font-bold text-gray-400">{label}</span>
      <span className={`text-[11px] font-bold tabular-nums ${c.text}`}>{cell.used}/{cell.cap}</span>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function BucketCell({ cells }: { cells: [CapCell, CapCell, CapCell] }) {
  return (
    <div className="flex flex-col gap-1.5 min-w-[180px]">
      <CapBar cell={cells[0]} label="Daily" />
      <CapBar cell={cells[1]} label="New" />
      <CapBar cell={cells[2]} label="NC" />
    </div>
  )
}

function StatusBadge({ status, since }: { status: Status; since: string }) {
  if (status === 'in') return (
    <div className="inline-flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />in · {since}
    </div>
  )
  if (status === 'break') return (
    <div className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />break
    </div>
  )
  return <div className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-gray-300" />out</div>
}

export default function WorkloadPage() {
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [loading, setLoading] = useState(true)

  function load() {
    setLoading(true)
    rrGetWorkload().then(res => {
      setAgents((res?.data ?? []) as AgentRow[])
    }).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <Gauge className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Agent Workload</h1>
            <p className="text-sm text-gray-500 mt-0.5">Live cap usage per agent across all three buckets.</p>
          </div>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
        <div className="grid grid-cols-[220px_40px_200px_200px_200px] gap-0 px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 bg-gray-50">
          <span>Agent</span>
          <span />
          <span className="text-center">
            <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md">ABC</span>
          </span>
          <span className="text-center">
            <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-md">Reorder</span>
          </span>
          <span className="text-center">
            <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-md">RTO</span>
          </span>
        </div>

        {loading
          ? <div className="flex justify-center items-center h-48"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
          : agents.length === 0
            ? <div className="flex justify-center items-center h-32 text-gray-400 text-sm">No agents found</div>
            : <div className="divide-y divide-gray-50 overflow-y-auto flex-1">
              {agents.map(a => (
                <div key={a.user_id} className="grid grid-cols-[220px_40px_200px_200px_200px] gap-0 px-5 py-4 items-center">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(a.name) }}>{initials(a.name)}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{a.name}</div>
                      <StatusBadge status={a.status} since={a.since} />
                    </div>
                  </div>
                  <div />
                  <BucketCell cells={a.abc} />
                  <BucketCell cells={a.reorder} />
                  <BucketCell cells={a.rto} />
                </div>
              ))}
            </div>
        }
      </div>

      <div className="mt-3 flex items-start gap-2 text-xs text-gray-400">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-300" />
        Daily cap resets at midnight. New Lead &amp; Not-Connected caps are rolling windows. Amber = ≥70%, red = at cap.
      </div>
    </div>
  )
}
